import { uniq } from "@-/com.npmjs.array-utils-ts";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@-/com.npmjs.tanstack__react-query";
import arrayLength from "@-/com.npmjs.extra-array__length";
import useLatest from "@-/com.npmjs.use-latest";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useMorphemeCorpusFilter } from "@-/dev.improve.haniverse.lib.morpheme.corpus.use";
import {
  morphemeDetailsTileSliceQueryFn,
  useGetMorphemeDetailsTileSliceQueryKey,
} from "#get-morpheme-details-tile-slice";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Compounds revealed per `revealMore`. ~10 rows of the 4-column phone grid, and comfortably under
 * the 64-lookup DataLoader batch the slice read is bounded to — so one page is one batched DB read.
 */
export const MORPHEME_COMPOUNDS_PAGE_SIZE = 40;

type CompoundCardsPage = {
  offset: number;
  slices: (MorphemeDetailsTileSlice | null)[];
  /** The page's own snapshot of the compound count, so paging never reads a stale outer value. */
  total: number;
};

const EMPTY_CARDS: ReadonlyMap<string, MorphemeDetailsTileSlice | null> = new Map();
const EMPTY_CHARACTERS: string[] = [];

export type RevealedMorphemeCompoundCards = {
  /** Resolved cards for the revealed window, in `compoundCharacterList` order. */
  cards: MorphemeDetailsTileSlice[];
  /**
   * Every compound that uses this grapheme as a component, ordered and de-duplicated. Restricted
   * to the user's browsing corpus (`useMorphemeCorpusFilter`) unless `includeRareCompounds`.
   */
  compoundCharacterList: string[];
  /** More compounds are still hidden — render a "more below" footer and wire `revealMore`. */
  hasMore: boolean;
  /** The revealed window has cards still resolving; the section shows its header spinner. */
  isCardsPending: boolean;
  /** The compound character list itself is still loading. */
  isPending: boolean;
  /**
   * How many compounds fall outside the browsing corpus — the ones `includeRareCompounds` adds.
   * Counted either way, so a "show rarer characters" control knows whether it would change
   * anything; `undefined` until the compound list has loaded.
   */
  rareCompoundCount: number | undefined;
  /** Grow the revealed window by one page (clamped to the compound count). Stable identity. */
  revealMore: () => void;
};

/**
 * Every grapheme that contains `graphemeId` as a component, revealed a page at a time. Compounds
 * outside the user's browsing corpus are dropped BEFORE paging (unless `includeRareCompounds`),
 * so every page stays full and the list length is the count the section reports.
 *
 * A common component is a component of a LOT of characters — 艹 is in 1200, 氵 1192, 木 1145, and 41
 * components exceed 200. Resolving a card for each one up front (one `useQueries` entry per
 * compound) and mounting a tile for each in the unvirtualized grid is what took the info panel's
 * Compounds tab down on iOS.
 *
 * Nothing is truncated: `revealMore` keeps growing the window until every compound is rendered, so
 * a reader who scrolls far enough still sees all 1200. Two things grow together, the
 * `useDeckSectionCards` pattern:
 * - the REVEALED window (`revealMore`, wired to the section's `onEndReached`): how many compounds
 *   are rendered at all.
 * - the CARDS for that window, paged in by an infinite query that keeps fetching while fewer than
 *   the revealed count have resolved.
 *
 * The window survives a refetch (it is only clamped to the current compound count), and the cards
 * are keyed by character rather than paired positionally, so a changing list can never show one
 * compound's card under another's glyph.
 */
export function useRevealedMorphemeCompoundCards({
  enabled = true,
  graphemeId,
  includeRareCompounds = false,
}: {
  /** Gate the reads (e.g. a collapsed section) — reveal state is kept either way. */
  enabled?: boolean;
  graphemeId: number;
  /** List every compound, including those outside the browsing corpus. */
  includeRareCompounds?: boolean;
}): RevealedMorphemeCompoundCards {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const { cacheKey: corpusCacheKey, isGraphemeInCorpus } = useMorphemeCorpusFilter();
  // Every compound, user-independent; the corpus restriction is applied on top of it below.
  const allCompoundCharacterListQueryOptions = queryOptions({
    queryFn: async () => {
      const componentRows = await staticDb(import.meta.location).query.graphemeComponent.findMany({
        columns: {},
        where: { componentGraphemeId: graphemeId },
        with: { grapheme: { columns: { glyph: true } } },
      });
      return uniq(componentRows.map((row) => row.grapheme.glyph));
    },
    queryKey: ["morpheme-info-composition-compound-characters", graphemeId, { staticDb }] as const,
    throwOnError: true,
  });
  const { data: allCompoundCharacterList = EMPTY_CHARACTERS, isPending: isCharacterListPending } =
    useQuery(allCompoundCharacterListQueryOptions);
  // What the list is restricted to: the corpus, or `null` for every compound. Every query that
  // depends on the restriction keys on this, never on the predicate (which key hashing drops).
  const listCorpusCacheKey = includeRareCompounds ? null : corpusCacheKey;
  const restrictCompoundCharacters = useCallback(
    (characters: string[]) =>
      includeRareCompounds ? characters : characters.filter((glyph) => isGraphemeInCorpus(glyph)),
    [includeRareCompounds, isGraphemeInCorpus],
  );
  const corpusCompoundCharacterList = useMemo(
    () => allCompoundCharacterList.filter((glyph) => isGraphemeInCorpus(glyph)),
    [allCompoundCharacterList, isGraphemeInCorpus],
  );
  const compoundCharacterList = includeRareCompounds
    ? allCompoundCharacterList
    : corpusCompoundCharacterList;

  const [revealedCount, setRevealedCount] = useState(MORPHEME_COMPOUNDS_PAGE_SIZE);
  const totalCount = arrayLength(compoundCharacterList);
  const visibleCount = Math.min(revealedCount, totalCount);

  const getMorphemeDetailsTileSliceQueryKey = useGetMorphemeDetailsTileSliceQueryKey();
  const { data, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isFetchNextPageError } =
    useInfiniteQuery(
      infiniteQueryOptions({
        enabled: enabled && totalCount > 0,
        // A failed page must reach the surface's retry boundary instead of leaving the reveal
        // window permanently pending with fewer cards than requested.
        throwOnError: true,
        // The character list is READ from its own query rather than closed over, so this page
        // sequence depends only on `graphemeId` and the restriction — see the `queryKey` note
        // below. It resolves instantly from cache: the `useQuery` above has already populated it.
        queryFn: async ({ client, pageParam }): Promise<CompoundCardsPage> => {
          const characterList = restrictCompoundCharacters(
            await client.fetchQuery(allCompoundCharacterListQueryOptions),
          );
          const pageCharacters = characterList.slice(
            pageParam,
            pageParam + MORPHEME_COMPOUNDS_PAGE_SIZE,
          );
          const slices = await Promise.all(
            pageCharacters.map((morpheme) =>
              client.fetchQuery(
                queryOptions({
                  queryFn: morphemeDetailsTileSliceQueryFn,
                  queryKey: getMorphemeDetailsTileSliceQueryKey({ morpheme }),
                }),
              ),
            ),
          );
          return { offset: pageParam, slices, total: arrayLength(characterList) };
        },
        initialPageParam: 0,
        // Derived from the page's own snapshot of the list, so this never reads an outer value that
        // could disagree with what the page actually fetched.
        getNextPageParam: (lastPage: CompoundCardsPage): number | undefined => {
          const nextOffset = lastPage.offset + MORPHEME_COMPOUNDS_PAGE_SIZE;
          return nextOffset >= lastPage.total ? undefined : nextOffset;
        },
        // Keyed by `graphemeId`, the restriction and the character-list query's OPTIONS, never by
        // the list itself. All are O(1) to hash: the options object carries its own small key
        // (`[…, graphemeId, { staticDb }]`). Spreading the resolved list in (as the deck's bounded
        // equivalent does) would build — and re-hash on every render — a key with one entry per
        // compound: ~1200 for a common component like 艹.
        // The key getter and the restriction predicate are per-render closures; they hash to `{}`
        // so a new identity never invalidates the sequence (`listCorpusCacheKey` stands in for the
        // predicate), but listing them keeps the dependencies explicit.
        queryKey: [
          "morphemeCompoundCards",
          graphemeId,
          listCorpusCacheKey,
          {
            allCompoundCharacterListQueryOptions,
            getMorphemeDetailsTileSliceQueryKey,
            restrictCompoundCharacters,
            staticDb,
          },
        ] as const,
      }),
    );

  const loadedCount = useMemo(
    () => data?.pages.reduce((count, page) => count + arrayLength(page.slices), 0) ?? 0,
    [data],
  );

  // Follow the revealed window: one page at a time, never past a failed page.
  useEffect(() => {
    if (
      enabled &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isError &&
      !isFetchNextPageError &&
      loadedCount < visibleCount
    ) {
      void fetchNextPage();
    }
  }, [
    enabled,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchNextPageError,
    isFetchingNextPage,
    loadedCount,
    visibleCount,
  ]);

  const cardByCharacter = useMemo(() => {
    if (data === undefined) {
      return EMPTY_CARDS;
    }
    const map = new Map<string, MorphemeDetailsTileSlice | null>();
    for (const page of data.pages) {
      for (const [index, slice] of page.slices.entries()) {
        const character = compoundCharacterList[page.offset + index];
        if (character !== undefined) {
          map.set(character, slice);
        }
      }
    }
    return map;
  }, [compoundCharacterList, data]);

  const cards = useMemo(
    () =>
      compoundCharacterList.slice(0, visibleCount).flatMap((character) => {
        const card = cardByCharacter.get(character);
        return card == null ? [] : [card];
      }),
    [cardByCharacter, compoundCharacterList, visibleCount],
  );

  const totalCountRef = useLatest(totalCount);
  const revealMore = useCallback(() => {
    setRevealedCount((count) =>
      count >= totalCountRef.current ? count : count + MORPHEME_COMPOUNDS_PAGE_SIZE,
    );
  }, [totalCountRef]);

  const isCardsPending = totalCount > 0 && loadedCount < visibleCount;

  return {
    cards,
    compoundCharacterList,
    hasMore: visibleCount < totalCount,
    isCardsPending,
    isPending: isCharacterListPending || isCardsPending,
    rareCompoundCount: isCharacterListPending
      ? undefined
      : arrayLength(allCompoundCharacterList) - arrayLength(corpusCompoundCharacterList),
    revealMore,
  };
}
